import { Link } from "react-router-dom";
import { ArrowRightIcon as ArrowRight, DatabaseIcon as Database, FlowArrowIcon as FlowArrow, LightningIcon as Lightning, StackIcon as Stack } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative border-b border-[var(--ml-border)] ml-grid-bg overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-20 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 lg:col-start-1">
            <div className="ml-eyebrow mb-6" data-testid="hero-eyebrow">
              IBM i Modernization · Architecture · Consulting
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[68px] leading-[1.02] tracking-tight font-semibold text-[var(--ml-text-primary)]">
              Modernize <span className="text-[var(--ml-primary)]">around</span> IBM i.
              <br />
              Not away from it.
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-[var(--ml-text-secondary)] max-w-2xl leading-relaxed">
              Forty years of business knowledge lives inside RPG, CL, DDS and DB2 for i.
              Modernization Lab is a senior architect — six of them, actually — that helps you expose,
              extend, and orchestrate that value with APIs, events, and AI agents.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/hub" className="ml-btn-primary" data-testid="hero-cta-hub">
                Enter the Agent Hub
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/deliverables" className="ml-btn-secondary" data-testid="hero-cta-deliverables">
                Generate a Deliverable
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 ml-mono text-xs uppercase tracking-widest text-[var(--ml-text-secondary)]">
              <span>RPG III / IV / LE</span>
              <span>·</span>
              <span>Infor XA · MAPICS</span>
              <span>·</span>
              <span>JDE · BPCS · System 21</span>
              <span>·</span>
              <span>DB2 for i</span>
            </div>
          </div>

          <div className="lg:col-span-5 lg:col-start-8">
            <div className="ml-card p-6 ml-shadow-card" data-testid="hero-framework-card">
              <div className="ml-eyebrow mb-4">Consulting Framework</div>
              <ol className="space-y-3">
                {[
                  "Identify the business problem",
                  "Identify affected processes",
                  "Identify IBM i objects involved",
                  "Identify risks",
                  "Recommend modernization options",
                ].map((step, i) => (
                  <li key={step} className="flex items-start gap-3">
                    <span className="ml-mono text-xs text-[var(--ml-primary)] mt-1">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-[15px] text-[var(--ml-text-primary)]">{step}</span>
                  </li>
                ))}
              </ol>
              <div className="border-t border-[var(--ml-border)] mt-5 pt-5 grid grid-cols-3 gap-2 text-center text-xs">
                <Tier label="Minimum" />
                <Tier label="Moderate" />
                <Tier label="Strategic" emphasis />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="border-b border-[var(--ml-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <div className="ml-eyebrow mb-3">Core Principles</div>
              <h2 className="text-3xl lg:text-4xl font-medium tracking-tight">
                Business value first.
                <br />
                Technology second.
              </h2>
              <p className="mt-5 text-[var(--ml-text-secondary)] leading-relaxed">
                Eight principles guide every recommendation. Read them once and you will recognize
                a Modernization Lab plan when you see one.
              </p>
            </div>
            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {PRINCIPLES.map((p, i) => (
                <div key={p} className="ml-card p-5" data-testid={`principle-${i}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="ml-mono text-xs text-[var(--ml-primary)]">
                      0{i + 1}
                    </span>
                    <div className="h-px flex-1 bg-[var(--ml-border)]" />
                  </div>
                  <p className="text-[15px] text-[var(--ml-text-primary)] leading-snug">{p}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AGENT TEASER */}
      <section className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-7">
              <div className="ml-eyebrow mb-3">Six Specialists. One Lab.</div>
              <h2 className="text-3xl lg:text-4xl font-medium tracking-tight">
                Talk to the architect, the code analyst,
                <br />
                or the forensic specialist.
              </h2>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/hub" className="ml-btn-primary" data-testid="teaser-cta">
                Browse all 6 agents
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {AGENT_NAMES.map((n) => (
              <div
                key={n}
                className="ml-card p-4 text-sm"
                data-testid={`agent-teaser-${n.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-2">
                  Agent
                </div>
                <div className="font-medium leading-tight">{n}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARCHITECTURE LAYERS */}
      <section className="border-b border-[var(--ml-border)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20">
          <div className="ml-eyebrow mb-3">Agentic Modernization Philosophy</div>
          <h2 className="text-3xl lg:text-4xl font-medium tracking-tight max-w-3xl">
            Four layers. IBM i stays the system of record.
          </h2>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-4 gap-4">
            {LAYERS.map((l) => (
              <div key={l.title} className="ml-card p-6 flex flex-col gap-3" data-testid={`layer-${l.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-9 h-9 bg-[var(--ml-surface)] border border-[var(--ml-border)] flex items-center justify-center">
                  <l.Icon size={18} weight="bold" color="#0f62fe" />
                </div>
                <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
                  {l.label}
                </div>
                <div className="font-medium text-[17px] tracking-tight">{l.title}</div>
                <p className="text-sm text-[var(--ml-text-secondary)] leading-relaxed">{l.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--ml-text-primary)] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="ml-eyebrow text-white/60 mb-3">Start a Modernization Conversation</div>
            <h2 className="text-3xl lg:text-4xl font-medium tracking-tight">
              Bring your toughest IBM i question.
              <br />
              <span className="text-white/70">A senior architect is listening.</span>
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/hub" className="ml-btn-primary" data-testid="footer-cta">
              Open the Lab
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Tier({ label, emphasis }) {
  return (
    <div
      className={`py-2 border ${emphasis ? "border-[var(--ml-primary)] bg-[var(--ml-primary)]/[0.06] text-[var(--ml-primary)]" : "border-[var(--ml-border)] text-[var(--ml-text-secondary)]"}`}
    >
      <div className="ml-mono uppercase tracking-widest text-[10px]">{label}</div>
    </div>
  );
}

const PRINCIPLES = [
  "Business value comes before technology.",
  "Modernization is preferred over replacement.",
  "Decades of business knowledge live in IBM i.",
  "APIs beat screen scraping.",
  "Data integrity over visual modernization.",
  "Journaled business events are strategic assets.",
  "Every effort improves observability.",
  "Cost, risk, complexity, maintainability — always.",
];

const AGENT_NAMES = [
  "Modernization Architect",
  "IBM i Code Analyst",
  "ERP Process Analyst",
  "Integration Architect",
  "Agentic Advisor",
  "Legacy Forensics",
];

const LAYERS = [
  { label: "System of", title: "Record", body: "IBM i — DB2 for i, journals, RPG service programs. The single source of business truth.", Icon: Database },
  { label: "System of", title: "Engagement", body: "Web, mobile, and partner integrations consuming governed APIs.", Icon: Stack },
  { label: "System of", title: "Intelligence", body: "AI agents, analytics, and behavioral monitoring built on journaled events.", Icon: Lightning },
  { label: "System of", title: "Action", body: "APIs, workflows, automation. Agents act through governance — never around it.", Icon: FlowArrow },
];
